import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AchievementPanel = ({ userStats, recentBadges }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  const periods = [
    { id: 'week', label: 'Semana' },
    { id: 'month', label: 'Mês' },
    { id: 'year', label: 'Ano' }
  ];

  const getBadgeIcon = (type) => {
    switch (type) {
      case 'attendance': return 'Calendar';
      case 'organizer': return 'Users';
      case 'payment': return 'CreditCard';
      case 'social': return 'Heart';
      case 'streak': return 'Zap';
      default: return 'Award';
    }
  };

  const getBadgeColor = (type) => {
    switch (type) {
      case 'attendance': return 'bg-success/10 text-success';
      case 'organizer': return 'bg-primary/10 text-primary';
      case 'payment': return 'bg-secondary/10 text-secondary';
      case 'social': return 'bg-accent/10 text-accent';
      case 'streak': return 'bg-warning/10 text-warning';
      default: return 'bg-muted/50 text-muted-foreground';
    }
  };

  const getStatIcon = (type) => {
    switch (type) {
      case 'matches': return 'Calendar';
      case 'wins': return 'Trophy';
      case 'goals': return 'Target';
      case 'reputation': return 'Star';
      default: return 'BarChart';
    }
  };

  const currentStats = userStats?.[selectedPeriod];
  const winRate = currentStats?.matches > 0 ? (currentStats?.wins / currentStats?.matches * 100) : 0;
  const reputationPercentage = (currentStats?.reputation / 5) * 100;

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-bold text-lg text-card-foreground">
            Conquistas
          </h2>
          <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
            {periods?.map((period) => (
              <button
                key={period?.id}
                onClick={() => setSelectedPeriod(period?.id)}
                className={`px-3 py-1 text-xs font-medium rounded transition-standard ${
                  selectedPeriod === period?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-card-foreground'
                }`}
              >
                {period?.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Stats Grid */}
      <div className="p-4 border-b border-border">
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg mx-auto mb-2">
              <Icon name={getStatIcon('matches')} size={20} className="text-primary" />
            </div>
            <div className="text-2xl font-bold text-card-foreground">
              {currentStats?.matches}
            </div>
            <div className="text-xs text-muted-foreground">
              Peladas
            </div>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-success/10 rounded-lg mx-auto mb-2">
              <Icon name={getStatIcon('wins')} size={20} className="text-success" />
            </div>
            <div className="text-2xl font-bold text-card-foreground">
              {currentStats?.wins}
            </div>
            <div className="text-xs text-muted-foreground">
              Vitórias
            </div>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-secondary/10 rounded-lg mx-auto mb-2">
              <Icon name={getStatIcon('goals')} size={20} className="text-secondary" />
            </div>
            <div className="text-2xl font-bold text-card-foreground">
              {currentStats?.goals}
            </div>
            <div className="text-xs text-muted-foreground">
              Gols
            </div>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-warning/10 rounded-lg mx-auto mb-2">
              <Icon name={getStatIcon('reputation')} size={20} className="text-warning" />
            </div>
            <div className="text-2xl font-bold text-card-foreground">
              {currentStats?.reputation?.toFixed(1)}
            </div>
            <div className="text-xs text-muted-foreground">
              Reputação
            </div>
          </div>
        </div>

        {/* Progress Indicators */}
        <div className="mt-4 space-y-3">
          <div>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-muted-foreground">Taxa de Vitórias</span>
              <span className="font-medium text-card-foreground">
                {winRate?.toFixed(0)}%
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-success h-2 rounded-full transition-all duration-300"
                style={{ width: `${winRate}%` }}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-muted-foreground">Reputação</span>
              <div className="flex items-center space-x-1">
                {[1, 2, 3, 4, 5]?.map((star) => (
                  <Icon 
                    key={star}
                    name="Star" 
                    size={12} 
                    className={star <= currentStats?.reputation ? 'text-warning' : 'text-muted'} 
                  />
                ))}
              </div>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-warning h-2 rounded-full transition-all duration-300"
                style={{ width: `${reputationPercentage}%` }}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Recent Badges */}
      <div className="p-4">
        <h3 className="font-heading font-semibold text-card-foreground mb-3">
          Badges Recentes
        </h3>
        
        {recentBadges?.length > 0 ? (
          <div className="space-y-3">
            {recentBadges?.slice(0, 3)?.map((badge) => (
              <div key={badge?.id} className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${getBadgeColor(badge?.type)}`}>
                  <Icon name={getBadgeIcon(badge?.type)} size={16} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-card-foreground">
                    {badge?.title}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {badge?.description}
                  </p>
                </div>
                <div className="text-xs text-muted-foreground font-caption">
                  {badge?.earnedAt}
                </div>
              </div>
            ))}
            
            {recentBadges?.length > 3 && (
              <Button
                variant="ghost"
                size="sm"
                fullWidth
                className="text-primary hover:text-primary mt-2"
              >
                Ver todos os badges ({recentBadges?.length})
              </Button>
            )}
          </div>
        ) : (
          <div className="text-center py-6">
            <div className="flex items-center justify-center w-12 h-12 bg-muted/50 rounded-lg mx-auto mb-3">
              <Icon name="Award" size={20} className="text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">
              Nenhum badge conquistado ainda
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Participe de mais peladas para ganhar badges!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AchievementPanel;